/** The alert sounds of a strategy, chosen in one window (docs/alert_sounds.md): a sound for each of
 * Entry, Exit, Target and Alert, picked as on TradingView (groups, a play button on each sound) with a
 * Test button, and the sound files of the server's folder with Publish / Unpublish. Nothing changes until
 * "Save and apply": the sounds go to the strategy and the publish decisions to the server together.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { useServerSounds, SERVER_SOUNDS_KEY } from "@/hooks/useServerSounds";
import { describeError } from "@/lib/http";
import { MUTE_CHIME, playChime, unlockAudio } from "@/lib/notify";
import { FILE_PREFIX, playSound } from "@/lib/soundLibrary";
import {
  getStrategySound,
  setStrategySound,
  SOUND_EVENTS,
  type SoundEvent,
  type SoundMode,
} from "@/lib/soundPrefs";
import { soundName } from "@/lib/soundNames";
import { soundsApi, type ServerSound, type SoundStatus } from "@/lib/soundsApi";
import { SoundPicker } from "./SoundPicker";

type Chosen = Record<SoundEvent, string>;
type Staged = Record<string, SoundStatus>;

function EventRow({
  label,
  value,
  files,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  files: readonly ServerSound[];
  onChange: (id: string) => void;
}>): ReactElement {
  return (
    <div className="grid grid-cols-[5rem_1fr_auto] items-center gap-2">
      <span className="label">{label}</span>
      <SoundPicker label={`${label} sound`} value={value} files={files} onChange={onChange} />
      <button
        type="button"
        className="btn btn-secondary"
        disabled={value === MUTE_CHIME}
        aria-label={`Test ${label} sound`}
        onClick={() => {
          unlockAudio();
          playChime("entry", value);
        }}
      >
        <i className="ti ti-volume text-base" />
        Test
      </button>
    </div>
  );
}

function FileRow({
  sound,
  status,
  onToggle,
}: Readonly<{ sound: ServerSound; status: SoundStatus; onToggle: () => void }>): ReactElement {
  const draft = status === "draft";
  return (
    <li className="flex items-center gap-3 py-1 text-sm">
      <button
        type="button"
        className="btn btn-secondary"
        aria-label={`Play ${sound.label}`}
        onClick={() => {
          unlockAudio();
          playSound(`${FILE_PREFIX}${sound.id}`);
        }}
      >
        <i className="ti ti-player-play text-base" />
      </button>
      <span className="min-w-36 flex-1">{sound.label}</span>
      <span className="text-xs text-ink-muted">{sound.group}</span>
      <span className={`w-20 text-xs ${draft ? "text-warning" : "text-success"}`}>
        {draft ? "Draft" : "Published"}
      </span>
      <button type="button" className={draft ? "btn btn-primary" : "btn"} onClick={onToggle}>
        {draft ? "Publish" : "Unpublish"}
      </button>
    </li>
  );
}

function useApply(strategyId: string, onApplied: () => void, onClose: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ chosen, staged }: { chosen: Chosen; staged: Staged }) => {
      for (const [id, status] of Object.entries(staged)) await soundsApi.setStatus(id, status);
      for (const { id } of SOUND_EVENTS) setStrategySound(strategyId, id, chosen[id]);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SERVER_SOUNDS_KEY });
      onApplied();
      onClose();
    },
  });
}

/** A chosen file that would no longer be published: the reason Save is held back, or null. */
function problemOf(chosen: Chosen, files: readonly ServerSound[], staged: Staged): string | null {
  for (const { id, label } of SOUND_EVENTS) {
    const path = chosen[id].startsWith(FILE_PREFIX) ? chosen[id].slice(FILE_PREFIX.length) : null;
    const file = files.find((f) => f.id === path);
    if (file !== undefined && (staged[file.id] ?? file.status) !== "published") {
      return `${label} uses ${file.label}, which is not published: publish it or pick another sound.`;
    }
  }
  return null;
}

function DialogFooter({
  blocked,
  onCancel,
  onApply,
}: Readonly<{ blocked: boolean; onCancel: () => void; onApply: () => void }>): ReactElement {
  return (
    <>
      <button type="button" className="btn" onClick={onCancel}>
        Cancel
      </button>
      <button type="button" className="btn btn-primary" disabled={blocked} onClick={onApply}>
        Apply
      </button>
    </>
  );
}

function FilesSection({
  all,
  status,
  onStage,
}: Readonly<{
  all: readonly ServerSound[];
  status: (s: ServerSound) => SoundStatus;
  onStage: (id: string, next: SoundStatus) => void;
}>): ReactElement {
  return (
    <>
      <h4 className="mt-5 text-sm font-medium">Sound files</h4>
      <p className="mb-1 text-xs text-ink-muted">
        Files put in the server&apos;s sounds folder are drafts until you publish them here; a
        published file is offered in the pickers above and on the chart.
      </p>
      {all.length === 0 && (
        <p className="text-xs text-ink-secondary">No sound files in the folder.</p>
      )}
      <ul className="max-h-48 overflow-auto">
        {all.map((s) => (
          <FileRow
            key={s.id}
            sound={s}
            status={status(s)}
            onToggle={() => {
              onStage(s.id, status(s) === "draft" ? "published" : "draft");
            }}
          />
        ))}
      </ul>
    </>
  );
}

function useChosen(strategyId: string, mode: SoundMode) {
  return useState<Chosen>(() => ({
    entry: getStrategySound(strategyId, "entry", mode),
    exit: getStrategySound(strategyId, "exit", mode),
    target: getStrategySound(strategyId, "target", mode),
    alert: getStrategySound(strategyId, "alert", mode),
  }));
}

function EventRows({
  chosen,
  files,
  onChange,
}: Readonly<{
  chosen: Chosen;
  files: readonly ServerSound[];
  onChange: (event: SoundEvent, sound: string) => void;
}>): ReactElement {
  return (
    <div className="grid gap-3">
      {SOUND_EVENTS.map(({ id, label }) => (
        <EventRow
          key={id}
          label={label}
          value={chosen[id]}
          files={files}
          onChange={(sound) => {
            onChange(id, sound);
          }}
        />
      ))}
    </div>
  );
}

function Notes({
  problem,
  error,
  chosen,
  all,
}: Readonly<{
  problem: string | null;
  error: string | null;
  chosen: Chosen;
  all: readonly ServerSound[];
}>): ReactElement {
  const now = SOUND_EVENTS.map(({ id, label }) => `${label} ${soundName(chosen[id], all)}`).join(
    " · ",
  );
  return (
    <>
      {problem !== null && <p className="mt-2 text-xs text-warning">{problem}</p>}
      {error !== null && <p className="mt-2 text-xs text-danger">Not saved: {error}</p>}
      <p className="mt-1 text-[11px] text-ink-muted">Now: {now}</p>
    </>
  );
}

/** What the window will make of the files once Apply is pressed: each file's status, the published ones, and any problem. */
function stateOf(all: readonly ServerSound[], chosen: Chosen, staged: Staged) {
  const status = (s: ServerSound): SoundStatus => staged[s.id] ?? s.status;
  return {
    status,
    published: all.filter((s) => status(s) === "published"),
    problem: problemOf(chosen, all, staged),
  };
}

const TITLE = {
  title: "Alert sounds",
  description: "Pick a sound for each event and press Apply: nothing changes before that.",
  icon: "ti-music",
} as const;

interface DialogProps {
  strategyId: string;
  /** whether the strategy is live or paper: they start with different sounds */
  mode: SoundMode;
  onClose: () => void;
  onApplied: () => void;
}

export function AlertSoundsDialog({
  strategyId,
  mode,
  onClose,
  onApplied,
}: Readonly<DialogProps>): ReactElement {
  const all = useServerSounds().data ?? [];
  const [chosen, setChosen] = useChosen(strategyId, mode);
  const [staged, setStaged] = useState<Staged>({});
  const apply = useApply(strategyId, onApplied, onClose);
  const { status, published, problem } = stateOf(all, chosen, staged);
  return (
    <Modal
      open
      size="xl"
      {...TITLE}
      onClose={onClose}
      footer={
        <DialogFooter
          blocked={problem !== null || apply.isPending}
          onCancel={onClose}
          onApply={() => {
            apply.mutate({ chosen, staged });
          }}
        />
      }
    >
      <EventRows
        chosen={chosen}
        files={published}
        onChange={(event, sound) => {
          setChosen({ ...chosen, [event]: sound });
        }}
      />
      <FilesSection
        all={all}
        status={status}
        onStage={(id, next) => {
          setStaged({ ...staged, [id]: next });
        }}
      />
      <Notes
        problem={problem}
        error={apply.isError ? describeError(apply.error) : null}
        chosen={chosen}
        all={all}
      />
    </Modal>
  );
}
